// packages/shared-components/src/components/Slider/Slider.tsx
import { useState, useRef, useEffect } from "react";
import { SliderProps } from "./types";
import "./Slider.scss";

/**
 * 滑块组件
 *
 * 支持受控/非受控模式，可选显示数值
 *
 * @example
 * ```tsx
 * // 基础用法
 * <Slider defaultValue={50} onChange={(v) => console.log(v)} />
 *
 * // 显示数值
 * <Slider
 *   defaultValue={50}
 *   showValue
 *   valueFormatter={(v) => `${v}%`}
 * />
 *
 * // 受控模式
 * <Slider value={value} onChange={setValue} />
 * ```
 */
export function Slider({
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  onChange,
  onChangeComplete,
  showValue = false,
  valueFormatter: _valueFormatter = (v) => String(v),
  unit = "",
  className = "",
  valueShowTopRight = false,
  scaleCount,
  scaleByStep = false,
}: SliderProps) {
  // 🔥 自动计算小数精度（根据 step 得出要保留几位小数）
  const getDecimalPlaces = (step: number) => {
    if (step >= 1) return 0;
    const stepStr = step.toString();
    const dotIndex = stepStr.indexOf(".");
    return dotIndex === -1 ? 0 : stepStr.length - dotIndex - 1;
  };

  const decimalPlaces = getDecimalPlaces(step);

  // 🔥 精确四舍五入（干掉浮点脏值）
  const roundToPrecision = (num: number) => {
    const p = Math.pow(10, decimalPlaces);
    return Math.round(num * p) / p;
  };

  // 按 step 吸附到最近档位（以 min 为基准）
  const snapToStep = (num: number) => {
    const safeStep = step > 0 ? step : 1;
    const snapped = min + Math.round((num - min) / safeStep) * safeStep;
    return roundToPrecision(snapped);
  };

  // 🔥 格式化显示：固定小数位数，不省略0
  const formatDisplay = (num: number) => {
    return num.toFixed(decimalPlaces);
  };

  const [scaleCfg, setScaleCfg] = useState({
    count: 10,
    maxMarks: [0, 5, 10],
    multiple: 10,
  });

  const [internalValue, setInternalValue] = useState(
    roundToPrecision(defaultValue)
  );
  const [inputStr, setInputStr] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const isControlled = controlledValue !== undefined;
  const value = isControlled
    ? roundToPrecision(controlledValue)
    : internalValue;

  const rawPercentage =
    max - min === 0 ? 0 : ((value - min) / (max - min)) * 100;
  const percentage = Math.max(0, Math.min(100, rawPercentage));

  // 🔥 拖动计算时也做精确取整
  const updateValue = (clientX: number) => {
    if (!trackRef.current || disabled) return;

    const rect = trackRef.current.getBoundingClientRect();
    const percent = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    );
    const rawValue = min + percent * (max - min);
    const steppedValue = snapToStep(rawValue);
    const clampedValue = Math.max(min, Math.min(max, steppedValue));

    if (!isControlled) {
      setInternalValue(clampedValue);
    }
    onChange?.(clampedValue);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    // 避免拖动起点冒泡到可点击父级（如 DPI 卡片）
    e.stopPropagation();
    isDragging.current = true;
    setDragging(true);
    updateValue(e.clientX);

    const bodyStyle = document.body.style as any;
    bodyStyle.userSelect = "none";
    bodyStyle.webkitUserSelect = "none";
    bodyStyle.msUserSelect = "none";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        e.preventDefault();
        updateValue(e.clientX);
      }
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      setDragging(false);
      const bodyStyle = document.body.style as any;
      bodyStyle.userSelect = "";
      bodyStyle.webkitUserSelect = "";
      bodyStyle.msUserSelect = "";
      onChangeComplete?.(value);
      const suppressClick = (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      document.addEventListener("click", suppressClick, true);
      window.setTimeout(() => {
        document.removeEventListener("click", suppressClick, true);
      }, 0);
    };

    const handleMouseLeave = () => {
      if (isDragging.current) handleMouseUp();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseleave", handleMouseLeave);
      const bodyStyle = document.body.style as any;
      bodyStyle.userSelect = "";
      bodyStyle.webkitUserSelect = "";
      bodyStyle.msUserSelect = "";
    };
  }, [min, max, step, disabled, onChange, onChangeComplete, value]);

  // 🔥 输入框提交：严格按精度清洗
  const commitInputValue = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      setInputStr(null);
      return;
    }
    let num = Number(trimmed);
    if (isNaN(num)) num = min;

    const steppedValue = snapToStep(num);
    const clampedValue = Math.max(min, Math.min(max, steppedValue));

    if (!isControlled) {
      setInternalValue(clampedValue);
    }
    onChange?.(clampedValue);
    onChangeComplete?.(clampedValue);
    setInputStr(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setInputStr(e.target.value);
  };

  const handleInputBlur = () => {
    if (inputStr !== null) {
      commitInputValue(inputStr);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputStr !== null) {
      commitInputValue(inputStr);
      (e.target as HTMLInputElement).blur();
    }
  };

  const scaleRef = useRef<HTMLDivElement>(null);
  const stepSegments =
    scaleByStep && step > 0 && max > min
      ? Math.max(1, Math.round((max - min) / step))
      : 0;
  const getScaleData = () => {
    if (scaleCount != null || scaleByStep) return;
    const clientWidth = scaleRef.current?.clientWidth ?? 0;
    const next =
      clientWidth > 600
        ? { count: 20, maxMarks: [0, 5, 10, 15, 20], multiple: 5 }
        : { count: 10, maxMarks: [0, 5, 10], multiple: 10 };
    setScaleCfg((prev) =>
      prev.count === next.count && prev.multiple === next.multiple ? prev : next
    );
  };

  useEffect(() => {
    getScaleData();
    if (scaleCount != null || scaleByStep) return;
    window.addEventListener("resize", getScaleData);
    return () => window.removeEventListener("resize", getScaleData);
  }, [scaleCount, scaleByStep]);

  const customScaleCount =
    scaleCount != null && scaleCount > 0 ? Math.floor(scaleCount) : 0;

  return (
    <div
      className={`el-slider-box ${disabled ? "disabled" : ""} ${dragging ? "dragging" : ""} ${className}`}
    >
      <div
        className="el-slider-control"
        ref={trackRef}
        onMouseDown={handleMouseDown}
      >
        <div className="el-slider-scale" ref={scaleRef}>
          {stepSegments > 1
            ? Array.from({ length: stepSegments - 1 }, (_, i) => (
                <span
                  key={i}
                  style={{
                    left: `${((i + 1) / stepSegments) * 100}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                />
              ))
            : customScaleCount > 0
              ? Array.from({ length: customScaleCount }, (_, i) => (
                  <span
                    key={i}
                    className="el-slider-scale-max"
                    style={{
                      left: `${((i + 1) / (customScaleCount + 1)) * 100}%`,
                    }}
                  />
                ))
              : Array.from({ length: scaleCfg.count }).map((_, i) => (
                  <span
                    key={i}
                    className={
                      scaleCfg.maxMarks.includes(i) ? "el-slider-scale-max" : ""
                    }
                    style={{ left: `${i * scaleCfg.multiple}%` }}
                  />
                ))}
        </div>
        <div
          className="el-slider-track"
          style={{
            width: `${percentage}%`,
            ["--ui-slider-fill" as string]: String(Math.max(percentage, 0.001)),
          }}
        />
        <div className="el-slider-thumb" style={{ left: `${percentage}%` }} />
      </div>

      {showValue && (
        <div
          className={
            valueShowTopRight
              ? "el-slider-input-unit top"
              : "el-slider-input-unit"
          }
        >
          <div className="el-slider-input">
            <input
              type="number"
              className="el-input-inner"
              min={min}
              max={max}
              step={step}
              value={inputStr !== null ? inputStr : formatDisplay(value)}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setInputStr(null)}
              disabled={disabled}
            />
          </div>
          <div className="el-slider-unit">{unit}</div>
        </div>
      )}
    </div>
  );
}
